import { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from 'controller/utils/@reduxjs/toolkit';
import {
  useInjectReducer,
  useInjectSaga,
} from 'controller/utils/redux-injectors';
import { success, error } from 'core/components/Notify';
import CustomResponse from 'core/utils/types/CustomResponse';
import { digitalMarketingSaga } from './saga';
import { DigitalMarketingState, BlogItem } from './types';

const isPublishedBlogItem = (blog: BlogItem) => {
  const value = blog.is_published
    ?? blog.IS_PUBLISHED
    ?? blog.is_web_published
    ?? blog.IS_WEB_PUBLISHED
    ?? blog.is_published_website
    ?? blog.IS_PUBLISHED_WEBSITE
    ?? blog.published
    ?? blog.PUBLISHED
    ?? blog.status
    ?? blog.STATUS;

  if (typeof value === 'boolean') {
    return value;
  }

  const normalizedValue = String(value ?? '').trim().toLowerCase();

  return value !== undefined
    && value !== null
    && normalizedValue !== ''
    && !['false', '0', 'no', 'unpublished', 'unpublish', 'draft'].includes(normalizedValue);
};

export const initialState: DigitalMarketingState = {
  blogsList: [],
  savedBlogs: [],
  publishedBlogs: [],
  currentBlog: undefined,
  
  totalCount: 0,
  hasNext: false,
  page: 1,
  pageSize: 10,
  publishFilter: 'both',

  isLoadingBlogs: false,
  errorBlogs: undefined,

  isLoadingBlogView: false,
  errorBlogView: undefined,

  isUpsertingBlog: false,
  upsertSuccess: false,
  upsertedBlogId: undefined,
  errorUpsertBlog: undefined,

  isGeneratingAIText: false,
  generatedTextData: undefined,
  errorGeneratingAIText: undefined,

  isGeneratingAIImage: false,
  generatedImageTask: undefined,
  generatedImageUrl: undefined,
  errorGeneratingAIImage: undefined,

  isEnhancingImage: false,
  errorEnhancingImage: undefined,

  isPublishingBlog: false,
  publishSuccess: false,
  lastPublishStatus: undefined,
  errorPublishingBlog: undefined,

  isDeletingBlog: false,
  deleteSuccess: false,
  errorDeletingBlog: undefined,
};

const slice = createSlice({
  name: 'digitalMarketing' as any,
  initialState,
  reducers: {
    // List
    fetchBlogsStart(
      state,
      action: PayloadAction<{ institute_id: number; branch_id: number; publish_filter?: string; page?: number; page_size?: number; search?: string }>
    ) {
      state.isLoadingBlogs = true;
      state.errorBlogs = undefined;
      if (action.payload.publish_filter) state.publishFilter = action.payload.publish_filter;
      if (action.payload.page) state.page = action.payload.page;
      if (action.payload.page_size) state.pageSize = action.payload.page_size;
    },
    fetchBlogsSuccess(state, action: PayloadAction<{ items: BlogItem[]; total_count: number; has_next: boolean }>) {
      state.isLoadingBlogs = false;
      state.blogsList = action.payload.items || [];
      state.savedBlogs = state.blogsList.filter(b => !isPublishedBlogItem(b));
      state.publishedBlogs = state.blogsList.filter(isPublishedBlogItem);
      state.totalCount = action.payload.total_count || 0;
      state.hasNext = action.payload.has_next || false;
    },
    fetchBlogsFailure(state, action: PayloadAction<CustomResponse>) {
      state.isLoadingBlogs = false;
      state.errorBlogs = action.payload;
    },

    // View
    fetchBlogByIdStart(state, action: PayloadAction<{ id: number }>) {
      state.isLoadingBlogView = true;
      state.errorBlogView = undefined;
    },
    fetchBlogByIdSuccess(state, action: PayloadAction<BlogItem>) {
      state.isLoadingBlogView = false;
      state.currentBlog = action.payload;
    },
    fetchBlogByIdFailure(state, action: PayloadAction<CustomResponse>) {
      state.isLoadingBlogView = false;
      state.errorBlogView = action.payload;
    },

    // Upsert
    upsertBlogStart(state, action: PayloadAction<any>) {
      state.isUpsertingBlog = true;
      state.upsertSuccess = false;
      state.upsertedBlogId = undefined;
      state.errorUpsertBlog = undefined;
    },
    upsertBlogSuccess(state, action: PayloadAction<any>) {
      state.isUpsertingBlog = false;
      state.upsertSuccess = true;
      state.upsertedBlogId = action.payload?.blog_id ?? action.payload?.BLOG_ID ?? action.payload?.id ?? action.payload?.BRANCH_BLOG_ID;
      success('Blog saved successfully', true);
    },
    upsertBlogFailure(state, action: PayloadAction<CustomResponse>) {
      state.isUpsertingBlog = false;
      state.errorUpsertBlog = action.payload;
      error(action.payload, true);
    },

    // Publish
    publishBlogStart(state, action: PayloadAction<{ blog_id: number; is_published: boolean; Authorization?: string }>) {
      state.isPublishingBlog = true;
      state.publishSuccess = false;
      state.lastPublishStatus = action.payload.is_published;
      state.errorPublishingBlog = undefined;
    },
    publishBlogSuccess(state, action: PayloadAction<any>) {
      state.isPublishingBlog = false;
      state.publishSuccess = true;
    },
    publishBlogFailure(state, action: PayloadAction<CustomResponse>) {
      state.isPublishingBlog = false;
      state.errorPublishingBlog = action.payload;
      error(action.payload, true);
    },

    // Delete
    deleteBlogStart(state, action: PayloadAction<{ id: number; Authorization?: string }>) {
      state.isDeletingBlog = true;
      state.deleteSuccess = false;
      state.errorDeletingBlog = undefined;
    },
    deleteBlogSuccess(state, action: PayloadAction<any>) {
      state.isDeletingBlog = false;
      state.deleteSuccess = true;
    },
    deleteBlogFailure(state, action: PayloadAction<CustomResponse>) {
      state.isDeletingBlog = false;
      state.errorDeletingBlog = action.payload;
      error(action.payload, true);
    },

    // AI Text Generator
    generateAITextStart(state, action: PayloadAction<{ title: string; description: string }>) {
      state.isGeneratingAIText = true;
      state.errorGeneratingAIText = undefined;
      state.generatedTextData = undefined;
    },
    generateAITextSuccess(state, action: PayloadAction<any>) {
      state.isGeneratingAIText = false;
      state.generatedTextData = action.payload;
    },
    generateAITextFailure(state, action: PayloadAction<CustomResponse>) {
      state.isGeneratingAIText = false;
      state.errorGeneratingAIText = action.payload;
      error(action.payload, true);
    },

    // AI Image Generator
    generateAIImageStart(state, action: PayloadAction<{ title: string; description: string }>) {
      state.isGeneratingAIImage = true;
      state.errorGeneratingAIImage = undefined;
      state.generatedImageUrl = undefined;
      state.generatedImageTask = undefined;
    },
    generateAIImageTaskSuccess(state, action: PayloadAction<string>) {
      state.generatedImageTask = action.payload;
    },
    generateAIImageSuccess(state, action: PayloadAction<string>) {
      state.isGeneratingAIImage = false;
      state.generatedImageUrl = action.payload;
    },
    generateAIImageFailure(state, action: PayloadAction<CustomResponse>) {
      state.isGeneratingAIImage = false;
      state.errorGeneratingAIImage = action.payload;
      error(action.payload, true);
    },

    // AI Image Enhancer
    enhanceImageStart(state, action: PayloadAction<{ user_image_url: string }>) {
      state.isEnhancingImage = true;
      state.errorEnhancingImage = undefined;
    },
    enhanceImageSuccess(state, action: PayloadAction<string>) {
      state.isEnhancingImage = false;
      state.generatedImageUrl = action.payload; // Update URL to trigger reload
    },
    enhanceImageFailure(state, action: PayloadAction<CustomResponse>) {
      state.isEnhancingImage = false;
      state.errorEnhancingImage = action.payload;
      error(action.payload, true);
    },

    clearState(state) {
      state.upsertSuccess = false;
      state.publishSuccess = false;
      state.deleteSuccess = false;
      state.generatedTextData = undefined;
      state.generatedImageUrl = undefined;
      state.generatedImageTask = undefined;
      state.upsertedBlogId = undefined;
      state.lastPublishStatus = undefined;
      state.isEnhancingImage = false;
      state.errorEnhancingImage = undefined;
    },
  },
});

export const { actions: digitalMarketingActions } = slice;

export const useDigitalMarketingSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectSaga({ key: slice.name, saga: digitalMarketingSaga });
  return { actions: slice.actions };
};
